import { Component } from 'react';
import { thousandSeparated } from 'shared/util/formatting';
import { SliderFilter } from '../sliderFilter/SliderFilter';
import { FilterProps } from '../../Filters';

const range = (start: number, stop: number, step: number): number[] =>
    Array.from({ length: (stop - start) / step + 1 }, (_, i) => start + i * step);

const MIN_LABEL_VALUE = 0;
const MAX_LABEL_VALUE = 10000;
const MAX_FORMATTED_LABEL_VALUE = '10 000+';
const MAX_FILTER_VALUE = 999999;
const STEPS = [...range(0, 99, 50), ...range(100, 1999, 100), ...range(2000, 3999, 200), ...range(4000, 9999, 500), MAX_FILTER_VALUE];
const MAX_STEPS = STEPS.length - 1;

const stepToValue = (step: number): number => STEPS[step];
const valueToStep = (value: number): number | undefined => (isNaN(value) ? undefined : STEPS.findIndex((element) => element >= value));

type PriceRangeFilterState = {
    fromStep: number;
    toStep: number;
    fromLabel: string | null;
    toLabel: string | null;
};

export class PriceRangeFilter extends Component<FilterProps, PriceRangeFilterState> {
    constructor(props: FilterProps) {
        super(props);
        // @ts-ignore
        this.timeoutReference = null;
        const priceFrom = Number(this.props.filters.price_from);
        const priceTo = Number(this.props.filters.price_to);
        this.state = {
            fromStep: this.getStepPositionFromValue(priceFrom) || MIN_LABEL_VALUE,
            toStep: this.getStepPositionFromValue(priceTo) || MAX_STEPS,
            fromLabel: this.formatNumber(priceFrom) || this.formatNumber(MIN_LABEL_VALUE),
            toLabel: this.formatNumber(priceTo) || this.formatNumber(MAX_LABEL_VALUE),
        };
    }

    componentDidUpdate(prevProps: FilterProps) {
        const { price_from: prevPriceFrom, price_to: prevPriceTo } = prevProps.filters;
        const { price_from: priceFrom, price_to: priceTo } = this.props.filters;

        if (prevPriceFrom !== priceFrom && prevPriceTo !== priceTo) {
            this.setState({
                fromStep: this.getStepPositionFromValue(Number(priceFrom)) || MIN_LABEL_VALUE,
                toStep: this.getStepPositionFromValue(Number(priceTo)) || MAX_STEPS,
                fromLabel: this.formatNumber(Number(priceFrom)) || this.formatNumber(MIN_LABEL_VALUE),
                toLabel: this.formatNumber(Number(priceTo)) || this.formatNumber(MAX_LABEL_VALUE),
            });
        }
    }

    getStepPositionFromValue(value: number) {
        if (isNaN(value)) {
            return undefined;
        } else if (value > MAX_LABEL_VALUE) {
            return MAX_STEPS;
        } else {
            return valueToStep(value);
        }
    }

    formatNumber(number: number): string | null {
        if (isNaN(number)) {
            return null;
        } else if (number === 0) {
            return number.toString();
        } else if (number >= MAX_LABEL_VALUE) {
            return MAX_FORMATTED_LABEL_VALUE;
        } else {
            return number ? thousandSeparated(number) : null;
        }
    }

    onChange(stepPositions: number[]) {
        const [fromStep, toStep] = stepPositions;
        const fromLabel = this.formattedStepValue(fromStep);
        const toLabel = this.formattedStepValue(toStep);
        this.setState({
            fromStep,
            toStep,
            fromLabel,
            toLabel,
        });
        // @ts-expect-error
        if (this.timeoutReference) {
            // @ts-expect-error
            clearTimeout(this.timeoutReference);
        }
        // @ts-expect-error
        this.timeoutReference = setTimeout(() => {
            this.props.onFilterChanged({
                price_from: this.calculateValue(fromStep),
                price_to: this.calculateValue(toStep),
            });
            // @ts-expect-error
            this.timeoutReference = null;
        }, 400);
    }

    calculateValue(stepPosition?: number) {
        if (stepPosition === undefined) {
            return undefined;
        }
        const value = stepToValue(stepPosition);
        if (value >= MAX_LABEL_VALUE) {
            return MAX_FILTER_VALUE;
        } else {
            return value;
        }
    }

    formattedStepValue(stepPosition?: number) {
        const value = this.calculateValue(stepPosition);
        if (value === undefined) {
            return '';
        }
        return this.formatNumber(value) ?? '';
    }

    render() {
        return (
            <SliderFilter
                label={[this.state.fromLabel, this.state.toLabel]}
                getLabelForStep={(position: number) => this.formattedStepValue(position)}
                step={[this.state.fromStep, this.state.toStep]}
                stepCount={MAX_STEPS}
                onChange={(value: number[]) => this.onChange(value)}
                ariaLabel={[`Pris fra ${this.state.fromLabel}`, `Pris til ${this.state.toLabel}`]}
                data-testid="priceRangeFilter"
            />
        );
    }
}
